import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, fromEvent, interval, merge } from 'rxjs';
import { exhaustMap, filter, startWith, takeWhile } from 'rxjs/operators';

const POLL_INTERVAL_MS = 5 * 60_000; // 5 min
const FETCH_TIMEOUT_MS = 10_000; // 10 s — bound a hung request
const HASHED_MAIN_BUNDLE = /^main-[A-Z0-9]+\.js$/i;

/**
 * Detects when a new build has been deployed while the app is open and
 * surfaces the staleness as a signal the shell can read to prompt the
 * user to reload.
 *
 * Why this exists on top of the other cache-busting mechanisms:
 *   - `handleRouterNavigationError` (chunk-reload.utils) is *reactive* —
 *     it only fires when a `loadComponent` chunk import fails. A user
 *     who never navigates to a route whose chunk has changed keeps
 *     running the old build forever.
 *   - The tombstone service worker only intervenes on browsers that
 *     ever installed the legacy Angular SW. Returning users who joined
 *     after the SW was removed don't have it, and their HTTP-cached
 *     `index.html` (from before `Cache-Control: no-store` was applied,
 *     or in transit between a deploy and CloudFront invalidation
 *     propagation) keeps serving the old chunk hashes.
 *
 * Primary signal: compare the CI-generated deployment ID embedded in
 * the no-store `/index.html` with the value in the shell this app loaded.
 * The ID is published with the shell (which deploys last), so a release
 * cannot be announced before the new shell is available. It also detects
 * lazy-chunk-only changes even when `main.js` is unchanged. Older shells
 * without this field fall back to comparing the hashed main bundle.
 *
 * On detection we set `stale` to `true`. The shell renders a banner
 * with a Reload button — we do NOT auto-reload, because silent reloads
 * can lose in-progress work (open modals, form drafts). The stream
 * completes via `takeWhile` once stale is set, so polling stops cleanly
 * instead of firing no-op intervals for the rest of the session.
 *
 * Triggers:
 *   - Immediately on `start()` (via `startWith`).
 *   - Every 5 minutes thereafter (`interval`).
 *   - On `visibilitychange` when the tab returns to the foreground —
 *     the most common moment a user comes back to a long-open tab.
 *   - On `online` when the browser reports the network is back, so a
 *     user who was offline during a deploy catches up immediately.
 * `exhaustMap` dedupes overlapping triggers so a visibility-fire mid-
 * poll doesn't spawn a duplicate request.
 *
 * Each `fetch` is bounded by `AbortSignal.timeout(10_000)` so a hung
 * connection doesn't pin a pending promise across poll intervals.
 *
 * In development builds there is no injected deployment ID and the
 * bundle filename has no hash, so `start()` no-ops. On a production
 * origin, a warning is logged only if both signals are missing; that
 * catches a regression in marker injection and `angular.json` output
 * hashing before detection silently stops.
 *
 * `start()` is invoked once from `provideAppInitializer` in
 * `app.config.ts` rather than from a component lifecycle hook, which
 * is the Angular 20+ idiom for app-boot side effects and keeps the
 * service trivially mockable in unit tests (just don't call `start`).
 */
@Injectable({ providedIn: 'root' })
export class BuildVersionCheckService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _stale = signal(false);

  /** Becomes `true` once a newer deploy is detected on the server. */
  readonly stale = this._stale.asReadonly();

  private readonly currentBuildVersion = this.readBuildVersionFromDocument();
  private readonly currentMainBundle = this.detectCurrentMainBundle();
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    if (!this.currentBuildVersion && !this.currentMainBundle) {
      // Defensive: on a non-localhost origin this signals a build-config
      // regression — the injected marker is missing and output hashing
      // is disabled, so deploy detection would be inactive.
      // localhost dev runs are unhashed by design, so they're skipped.
      const host = this.document.location.hostname;
      if (host && host !== 'localhost' && host !== '127.0.0.1' && host !== '::1') {
        console.warn(
          '[BuildVersionCheckService] No deployment marker or hashed main bundle detected — ' +
            'deploy detection is inactive. Check marker injection and `outputHashing` in angular.json.'
        );
      }
      return;
    }

    const visible$ = fromEvent(this.document, 'visibilitychange').pipe(
      filter(() => this.document.visibilityState === 'visible')
    );
    const win = this.document.defaultView;
    const online$ = win ? fromEvent(win, 'online') : merge();

    merge(visible$, online$, interval(POLL_INTERVAL_MS))
      .pipe(
        startWith(0),
        exhaustMap(() => from(this.check())),
        // Stop polling once we've notified the shell — there's no
        // newer state to discover, and the user is about to reload.
        takeWhile(() => !this._stale(), true),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private async check(): Promise<void> {
    if (this._stale()) return;

    try {
      const res = await fetch('/index.html', {
        cache: 'reload',
        signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      });
      if (!res.ok) return;
      const html = await res.text();
      const deployedBuildVersion = this.extractBuildVersionFromHtml(html);
      const deployed = this.extractMainBundleFromHtml(html);

      const versionChanged =
        this.currentBuildVersion !== null &&
        deployedBuildVersion !== null &&
        deployedBuildVersion !== this.currentBuildVersion;
      const bundleChanged =
        this.currentMainBundle !== null && deployed !== null && deployed !== this.currentMainBundle;

      // The deployment ID catches lazy-chunk-only releases; the hashed
      // main bundle is an independent fallback and consistency check.
      // Never refresh automatically: the user may have an active draft.
      if (versionChanged || bundleChanged) this._stale.set(true);
    } catch {
      // Network blip, timeout, or AbortError — try again on the next tick.
    }
  }

  private readBuildVersionFromDocument(): string | null {
    return normalizeBuildVersion(
      this.document.querySelector('meta[name="build-version"]')?.getAttribute('content')
    );
  }

  private extractBuildVersionFromHtml(html: string): string | null {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    return normalizeBuildVersion(
      parsed.querySelector('meta[name="build-version"]')?.getAttribute('content')
    );
  }

  /**
   * Scans the page's `<script>` tags for the hashed main bundle Angular
   * emitted at build time. Restricted to same-origin URLs and to scripts
   * whose final path segment matches the bundle pattern, so a
   * third-party script whose URL happens to contain a similar substring
   * can't false-positive.
   */
  private detectCurrentMainBundle(): string | null {
    const origin = this.document.location.origin;
    for (const script of Array.from(this.document.scripts)) {
      const src = script.src;
      if (!src) continue;
      let scriptUrl: URL;
      try {
        scriptUrl = new URL(src, origin);
      } catch {
        continue;
      }
      if (scriptUrl.origin !== origin) continue;
      const filename = filenameOf(scriptUrl.href);
      if (filename && HASHED_MAIN_BUNDLE.test(filename)) return filename;
    }
    return null;
  }

  /**
   * Pulls the hashed main bundle filename out of a freshly-fetched
   * `index.html`. Anchored on the `/` or `"` boundary that precedes it
   * in real Angular output (`src="main-XXX.js"`, `href="main-XXX.js"`,
   * or `/main-XXX.js`), so any incidental occurrence elsewhere in the
   * markup can't be picked up by mistake.
   */
  private extractMainBundleFromHtml(html: string): string | null {
    const match = html.match(/[/"](main-[A-Z0-9]+\.js)/i);
    return match ? match[1] : null;
  }
}

function normalizeBuildVersion(version: unknown): string | null {
  return typeof version === 'string' && version.trim() ? version.trim() : null;
}

function filenameOf(url: string): string | null {
  try {
    const { pathname } = new URL(url);
    const idx = pathname.lastIndexOf('/');
    return idx === -1 ? pathname : pathname.slice(idx + 1);
  } catch {
    return null;
  }
}
